import React, { useEffect, useRef } from "react";
import { GenericSignal, SignalType } from "@types";
import { getClassName } from "@utils";
import { Avatar, AvatarSize, AvatarVariant } from "@taskpane/components";
import { ArrowIcon } from "./icons";
import css from "./SentimentChangedSignalContent.module.scss";

export interface SentimentChangedSignalContentProps {
  signal: GenericSignal<SignalType.SentimentDecreased | SignalType.SentimentIncreased>;
}

const SentimentChangedSignalContent: React.FC<SentimentChangedSignalContentProps> = ({
  signal,
}) => {
  const avatarWrapperRef = useRef<HTMLDivElement>(null);

  const sender = signal.eventMetadata?.carbonLogicParticipants?.sender;
  const senderName = sender?.name || "Person";
  const { avgOfPreviousTwo = 0, currentSentiment = 0 } = signal.eventMetadata?.sentiment || {};
  const previousSentimentMargin = `${((avgOfPreviousTwo + 1.5) / 3) * 100}%`;
  const currentSentimentMargin = `${((currentSentiment + 1.5) / 3) * 100}%`;

  const description = `${senderName}'s sentiment has changed to ${signal.eventName === SignalType.SentimentDecreased ? "negative" : "positive"}`;

  const attrs = {
    container: {
      className: css.container,
    },
    textWrapper: {
      className: css.textWrapper,
    },
    title: {
      className: css.title,
    },
    description: {
      className: css.description,
    },
    content: {
      className: css.content,
    },
    rangeWrapper: {
      className: css.rangeWrapper,
    },
    range: {
      className: css.range,
    },
    rangeTextWrapper: {
      className: css.rangeTextWrapper,
    },
    avatarWrapper: {
      ref: avatarWrapperRef,
      style: {
        marginLeft: previousSentimentMargin,
      },
      className: getClassName(
        css.avatarWrapper,
        sender?.isUserDomain ? css.avatarWrapperUserSide : css.avatarWrapperProspectSide
      ),
    },
    avatar: {
      size: AvatarSize.XL,
      variant: sender?.isUserDomain ? AvatarVariant.User : AvatarVariant.Prospect,
      src: sender?.avatar,
      name: senderName,
    },
  };

  useEffect(() => {
    const avatarWrapper = avatarWrapperRef.current;

    if (avatarWrapper) {
      avatarWrapper.style.marginLeft = currentSentimentMargin;
    }
  }, []);

  return (
    <div {...attrs.container}>
      <div {...attrs.textWrapper}>
        <span {...attrs.title}>Sentiment changed</span>
        <span {...attrs.description}>{description}</span>
      </div>
      <div {...attrs.content}>
        <div {...attrs.rangeWrapper}>
          <div {...attrs.range} />
          <div {...attrs.avatarWrapper}>
            <Avatar {...attrs.avatar} />
            <ArrowIcon />
          </div>
        </div>
        <div {...attrs.rangeTextWrapper}>
          <span>Negative</span>
          <span>Positive</span>
        </div>
      </div>
    </div>
  );
};

export default SentimentChangedSignalContent;
